import React from 'react';
import Button from '../Common/Button';
import FindingCard from './FindingCard';
import './DeadEnd.css';

/**
 * S5 — the dead end (Unit 4 build spec §D4, design brief §2). Reached only
 * from verified_autofix mode's needs_decision: two fix attempts still left
 * findings. The pipeline already picked the best draft tried (fewest
 * findings) and re-promoted it to the current receipt — this screen just
 * renders that choice plainly and offers the three next moves the brief
 * names, each priced where money actually changes hands.
 */
export default function DeadEnd({
  bestDraft, findings, advisoryFindings, attempts,
  options, onAcceptAnyway, onTryAgain, tryAgainEstimate, onGoToOutline, busy,
}) {
  const changePlan = options?.change_plan;
  const tryAgain = options?.try_again;

  return (
    <div className="er-deadend">
      <div className="er-deadend__head">
        <span className="er-deadend__eyebrow">Best draft after {attempts} attempt{attempts === 1 ? '' : 's'}</span>
        <h2 className="er-deadend__title">This one still has findings the inspectors won&rsquo;t clear.</h2>
        <p className="er-deadend__sub">
          Here&rsquo;s the closest draft it produced, and what&rsquo;s still open. You decide what happens next.
        </p>
      </div>

      <div className="er-deadend__body">
        <div className="er-deadend__draft-col">
          <div className="er-deadend__prose">
            {(bestDraft || '').split(/\n{2,}/).filter(Boolean).map((p, i) => (
              <p key={i} className="er-deadend__para">{p.trim()}</p>
            ))}
          </div>
        </div>
        <div className="er-deadend__findings-col">
          <div className="er-deadend__findings-title">Still open</div>
          {findings.map((f) => (
            <FindingCard key={f.id} finding={f} advisory={false} disabled onRule={() => {}} onHover={() => {}} onLeave={() => {}} />
          ))}
          {advisoryFindings.map((f) => (
            <FindingCard key={f.id} finding={f} advisory disabled onRule={() => {}} onHover={() => {}} onLeave={() => {}} />
          ))}
        </div>
      </div>

      <div className="er-deadend__actions">
        <div className="er-deadend__action">
          <Button variant="primary" size="medium" disabled={busy} onClick={onAcceptAnyway}>
            Accept anyway
          </Button>
          <span className="er-deadend__action-note">Keeps this draft as-is. No further charge.</span>
        </div>
        <div className="er-deadend__action">
          <Button variant="secondary" size="medium" disabled={busy} loading={busy} onClick={onTryAgain}>
            Try again
            {tryAgainEstimate && !tryAgainEstimate.missing_pricing && ` · est. $${tryAgainEstimate.estimate_usd.toFixed(2)}`}
          </Button>
          <span className="er-deadend__action-note">
            {tryAgain?.how || 'Runs a fresh attempt from the same plan.'}
          </span>
        </div>
        <div className="er-deadend__action">
          <button type="button" className="er-deadend__action-link" onClick={onGoToOutline}>
            Change the plan →
          </button>
          <span className="er-deadend__action-note">
            {changePlan?.how || 'Change the outline or its moments, then try again.'}
          </span>
        </div>
      </div>
    </div>
  );
}
